import isEqual from 'lodash/isEqual';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Modal, ModalBody, ModalFooter } from 'react-bootstrap';
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import Select from 'react-select';
import { toast } from 'react-toastify';
import Api from '../../services/Api';
import useForm from '../../services/Utils/useForm';
import DataTable from '../DataTable';
import * as urls from '../shared/urls';
import { ApiResponse } from 'apisauce';
import { GlobalSearchResultGrid } from './GlobalSearchResultGrid';
import SplitCaseButton from '../SplitCaseButton/SplitCaseButton';
import ModalHeader from 'react-bootstrap/esm/ModalHeader';
import './GlobalSearch.scss';
import { getTableValuesByGroupName } from '../../services/Utils/IndexedDBUtils';
import { ICounty, IReport } from '../../common/sharedTypes';

type GlobalSearchRequest = {
  crashDateStart: moment.Moment;
  crashDateEnd: moment.Moment;
  crashKeys: string[];
  lastName: string;
  firstName: string;
  driverLicenseNumber: string;
  countyCode: string;
};

export type GlobalSearchResult = {
  reportId: number;
  report: IReport;
  driverLicenseNumbers: string[];
  driverNames: string[];
  dateOfCrash: string;
  originalCrashKey: string;
  reportType: string;
  streetNameOrHighwayNumber: string;
  county: string;
  agencyCaseNumber: string;
  workflowItemId: number;
  canSplit: boolean;
  openWorkflowItemStepId: number;
}

type GlobalSearchResponse = Array<GlobalSearchResult>;

export default function GlobalSearch() {
  const defaultValues = {
    crashKey1: '', // TODO actually, make this an array of crash keys
    crashKey2: '',
    lastName: '',
    firstName: '',
    driverLicenseNumber: '',
  };
  type FormValues = typeof defaultValues;
  const [values, updateValue, resetToDefaultValues] = useForm<FormValues>(defaultValues);
  const [crashDates, setCrashDates] = useState<[moment.Moment, moment.Moment]>([null, null]);
  const [isMergeSelectedDisabled, setIsMergeSelectedDisabled] = useState(true);
  const [isSplitDisabled, setIsSplitDisabled] = useState(true);
  const [isDeleteSelectedDisabled, setIsDeletedSelectedDisabled] = useState(true);
  const [searchResults, setSearchResults] = useState<GlobalSearchResponse>([]);
  const [counties, setCounties] = useState<ICounty[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<{ label: string, value: string }>();
  const dataTableRef = useRef<DataTable>();

  function setDisabledButtons(selectedRows: GlobalSearchResult[]) {
    setIsSplitDisabled(selectedRows.length !== 1 || !selectedRows[0].canSplit);
    setIsMergeSelectedDisabled(selectedRows.length !== 2);
    setIsDeletedSelectedDisabled(selectedRows.length === 0);
  }

  function onResultGridSelect(e, dt, type, indexes) {
    const selectedRows: any[] = dt.rows('.selected').data();
    setDisabledButtons(selectedRows);
  }

  function onResultGridDeselect(e, dt, type, indexes) {
    const selectedRows: any[] = dt.rows('.selected').data();
    setDisabledButtons(selectedRows);
  }

  useEffect(() => {
    dataTableRef.current?.currentTable.on('select', onResultGridSelect);
    dataTableRef.current?.currentTable.on('deselect', onResultGridDeselect);

    return () => {
      dataTableRef.current?.currentTable.off('select', onResultGridSelect);
      dataTableRef.current?.currentTable.off('deselect', onResultGridDeselect);
    }
  });

  useEffect(() => {
    if (counties.length === 0) {
      getTableValuesByGroupName("County").then((results) => setCounties(results));
    }
  }, [])

  const isSearchDisabled = isEqual(values, defaultValues)
    && !crashDates[0]
    && !crashDates[1]
    && !selectedCounty;

  const onSearchClick = () => {
    setSearchResults([]);
    setDisabledButtons([]);

    const searchRequest: GlobalSearchRequest = {
      crashDateStart: crashDates[0],
      crashDateEnd: crashDates[1],
      crashKeys: [values.crashKey1, values.crashKey2].filter(ck => ck && ck !== ''),
      lastName: values.lastName,
      firstName: values.firstName,
      driverLicenseNumber: values.driverLicenseNumber,
      countyCode: selectedCounty?.value,
    };

    Api.post(urls.GLOBAL_SEARCH, searchRequest)
      .then((response: ApiResponse<GlobalSearchResponse>) => {
        if (response.ok && response.data) {
          setSearchResults(response.data);
        }
      });
  }

  const removeFromResults = (id: number) => {
    const newSearchResults = searchResults.filter(x => x.reportId != id);
    setSearchResults(newSearchResults);
  }

  const onDeleteSelectedClick = async () => {
    const selectedRows = getSelectedRows();

    let deletedCount = 0;
    for (let i = 0; i < selectedRows.length; i++) {
      const row = selectedRows[i];
      const response = await Api.post(urls.SET_ACTIVE_STATUS, {
        workflowItemId: row.workflowItemId,
      });
      if (!response.ok) {
        toast.error(`Failed to delete ${row.originalCrashKey}`);
      } else {
        deletedCount += 1;
      }
    };

    if (deletedCount > 0) {
      toast.success(`Deleted ${deletedCount} report${deletedCount > 1 ? 's' : ''}`);
      onSearchClick();
    }
  }

  const onClearClick = () => {
    resetToDefaultValues();
    setCrashDates([null, null]);
    setSelectedCounty(null);

    setSearchResults([]);
  }

  function getSelectedRows(): GlobalSearchResult[] {
    const data = dataTableRef.current.getRowsForData(true).data();

    return data;
  }

  return (
    <div className="app">
      <div className='row'>
        <div className='col-sm-6'>
          <h3>Global Search</h3>
        </div>
      </div>

      <hr />

      <Form>
        <Form.Row> {/* search filters */}
          <Form.Group as={Col}>
            <Form.Label>Crash Date</Form.Label>
            <br />
            <DateRangePicker
              value={crashDates}
              onChange={(dates) => setCrashDates(dates)}
              rangeDivider=" to "
              openCalendarOnFocus={false}
              clearIcon={null}
              maxDate={new Date(2100, 12, 12)}
            />
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Crash Key 1</Form.Label>
            <Form.Control
              type="text"
              name="crashKey1"
              value={values.crashKey1}
              onChange={updateValue}
            />
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Crash Key 2</Form.Label>
            <Form.Control
              type="text"
              name="crashKey2"
              value={values.crashKey2}
              onChange={updateValue}
            />
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>County</Form.Label>
            <Select
              isClearable={true}
              name="selectedCounty"
              placeholder={"Select a county"}
              options={counties.map((county) => ({
                label: county.name + " - " + county.description,
                value: county.name,
              }))}
              onChange={setSelectedCounty}
              value={selectedCounty}
            />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col}>
            <Form.Label>First Name</Form.Label>
            <Form.Control
              name='firstName'
              type='text'
              value={values.firstName}
              onChange={updateValue}
            />
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              name='lastName'
              type='text'
              value={values.lastName}
              onChange={updateValue}
            />
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Driver License Number</Form.Label>
            <Form.Control
              name='driverLicenseNumber'
              type='text'
              value={values.driverLicenseNumber}
              onChange={updateValue}
            />
          </Form.Group>

          <Form.Group as={Col}></Form.Group>
        </Form.Row>

        <Form.Row className="flex-column">
          <Form.Group className="d-flex ml-1" style={{ gap: 5 }}>
            <Button
              variant="primary"
              disabled={isSearchDisabled}
              onClick={onSearchClick}
            >
              Search
            </Button>

            <MergeButton
              getSelectedRows={getSelectedRows}
              disabled={isMergeSelectedDisabled}
              onAfterMerge={removeFromResults}
            />

            <SplitCaseButton
              reportId={!isSplitDisabled && getSelectedRows()[0].reportId}
              disabled={isSplitDisabled}
              afterSplit={onSearchClick}
              openWorkflowItemStepId={!isSplitDisabled && getSelectedRows()[0].openWorkflowItemStepId}
              buttonText="Split Report"
            />

            <Button
              variant="danger"
              disabled={isDeleteSelectedDisabled}
              onClick={onDeleteSelectedClick}
            >
              Delete Selected
            </Button>
          </Form.Group>

          <Form.Group className="d-flex ml-1" style={{ gap: 5 }}>
            <Button
              variant='info'
              onClick={onClearClick}
            >
              Clear Results
            </Button>
          </Form.Group>
        </Form.Row>
      </Form>

      <div className='mt-4'>
        <GlobalSearchResultGrid results={searchResults} dataTableRef={dataTableRef} />
      </div>
    </div>
  )
}

type MergeButtonProps = {
  getSelectedRows: () => GlobalSearchResult[];
  disabled?: boolean;
  onAfterMerge?: (number) => void;
}

function MergeButton(props: MergeButtonProps) {
  const {
    getSelectedRows,
    disabled,
    onAfterMerge,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GlobalSearchResult[]>(null);

  const onMergeSelectedClick = () => {
    const selectedRowsData = getSelectedRows();
    const selectedRows = new Array(selectedRowsData[0], selectedRowsData[1]);
    setSelectedRows(selectedRows);
    setShowModal(true);
  }

  const onModalClose = () => {
    setShowModal(false);
  }

  return (
    <>
      <Button
        variant="secondary"
        disabled={disabled}
        onClick={onMergeSelectedClick}
      >
        Merge Selected
      </Button>

      <MergeModal
        show={showModal}
        onClose={onModalClose}
        selectedRows={selectedRows}
        onAfterMerge={onAfterMerge}
      />
    </>
  )
}

type MergeModalProps = {
  show: boolean;
  onClose: () => void;
  selectedRows: GlobalSearchResult[];
  onAfterMerge?: (number) => void;
}

function MergeModal(props: MergeModalProps) {
  const {
    show,
    onClose,
    selectedRows,
    onAfterMerge,
  } = props;

  const [newerReportRow, setNewerReportRow] = useState<GlobalSearchResult>(); // a.k.a. reportId1
  const [olderReportRow, setOlderReportRow] = useState<GlobalSearchResult>(); // a.k.a. reportId2
  const [merging, setMerging] = useState<boolean>();

  useEffect(() => {
    if (selectedRows?.length !== 2) return;

    const row1 = selectedRows[0];
    const row2 = selectedRows[1];

    const oldReportRow = row1.reportType === 'Investigator' ? row1
      : row2.reportType === 'Investigator' ? row2
        : row1.originalCrashKey < row2.originalCrashKey ? row1
          : row2
    const newReportRow = oldReportRow.reportId === row1.reportId ? row2 : row1;

    setNewerReportRow(newReportRow);
    setOlderReportRow(oldReportRow);
  }, [selectedRows])

  const onMakeParentClick = () => {
    const olderReportRowCopy = { ...olderReportRow };
    setOlderReportRow(newerReportRow);
    setNewerReportRow(olderReportRowCopy);
  }

  const onMergeClick = async () => {
    if (selectedRows.length !== 2) {
      toast.error('Only two reports can be merged together');
      return;
    }

    setMerging(true);

    const response = await Api.post(urls.GLOBAL_MERGE, {
      reportId1: newerReportRow.reportId,
      reportId2: olderReportRow.reportId
    })
      .finally(() => setMerging(false));

    if (!response.ok) {
      toast.error('Error merging reports. Contact support for more details.');
      return;
    }

    toast.success('Reports merged');
    onClose();

    if (onAfterMerge) onAfterMerge(olderReportRow.reportId);
  }

  const newOck = Math.min(...(([newerReportRow?.originalCrashKey, olderReportRow?.originalCrashKey]).map(Number)));

  return (
    <>
      <Modal
        show={show}
        aria-labelledby='contained-modal-title-vcenter'
        centered
        size='lg'
        onHide={onClose}
      >
        <ModalHeader closeButton>Choose Parent Report</ModalHeader>
        <ModalBody>
          <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'flex-start' }}>
            {olderReportRow &&
              <Card className='merge-card'>
                <Card.Header>Parent</Card.Header>
                <Card.Body>
                  <ul className='p-0 m-0 report-data'>
                    <li>
                      <h6>Crash Date</h6>
                      {olderReportRow.dateOfCrash}
                    </li>
                    <li>
                      <h6>Static Crash Key</h6>
                      {olderReportRow.originalCrashKey}
                    </li>
                    <li>
                      <h6>Report Type</h6>
                      {olderReportRow.reportType}
                    </li>
                    <li>
                      <h6>County</h6>
                      {olderReportRow.county}
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            }
            {newerReportRow &&
              <Card className='merge-card'>
                <Card.Header>
                  <Button
                    variant="secondary"
                    size='sm'
                    onClick={onMakeParentClick}
                  >
                    Make Parent
                  </Button>
                </Card.Header>
                <Card.Body>
                  <ul className='p-0 m-0 report-data'>
                    <li>
                      <h6>Crash Date</h6>
                      {newerReportRow.dateOfCrash}
                    </li>
                    <li>
                      <h6>Static Crash Key</h6>
                      {newerReportRow.originalCrashKey}
                    </li>
                    <li>
                      <h6>Report Type</h6>
                      {newerReportRow.reportType}
                    </li>
                    <li>
                      <h6>County</h6>
                      {newerReportRow.county}
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            }
          </div>
          <p className='mb-0 mt-3'>Report <b>{newerReportRow?.originalCrashKey}</b> will merge into <b>{olderReportRow?.originalCrashKey}</b>.  The Original Crash Key will be <b>{newOck}</b>.</p>
        </ModalBody>
        <ModalFooter>
          {merging && (
            <div>
              Processing Merge {"   "}
              <img
                src={require("../../../src/assets/SmartAdmin/img/32/Marty.gif")}
              />
            </div>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={merging}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={onMergeClick}
            disabled={merging}
          >
            Merge
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}
